import axios from "axios";
import { getConfig } from "../config/environment";

const config = getConfig();

const API_BASE_URL = config.apiUrl;

/* =======================
   Interfaces
======================= */

export interface GenerateDocumentRequest {
  prompt: string;
  fileIds: string[];
}

export interface Page {
  id: string;
  header?: string;
  body: string;
}

export interface AiResponse {
  pages: Page[];
}

export interface AiEditResponse {
  result: string;
}
export interface Document {
  id: string;
  name: string;
  type: string;
  path: string;
  size?: number;
  created_at?: string;
  s3_url: string;
  url?: string;
}

/* =======================
   Service Class
======================= */

class DocumentBuilderService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  constructor() {
    // Attach auth token automatically
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("auth_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Handle 401 globally
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("auth_token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  async generateAIDocument(data: GenerateDocumentRequest): Promise<AiResponse> {
    if (!data.prompt) throw new Error("Prompt is required");

    try {
      const response = await this.apiClient.post<AiResponse>(
        "/api/v1/document-builder/generate",
        data,
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail ||
          error.response?.data ||
          "Failed to generate document",
      );
    }
  }
  async editSelectedText(
    selectedText: string,
    instruction: string,
  ): Promise<string> {
    if (!selectedText) throw new Error("Selected text is required");
    if (!instruction) throw new Error("Instruction is required");

    try {
      const response = await this.apiClient.post<AiEditResponse>(
        "/api/v1/document-builder/ai-edit",
        { selectedText, instruction },
      );
      return response.data.result;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail ||
          error.response?.data ||
          "Failed to edit selected text",
      );
    }
  }

  async enhanceSelectedPage(page: Page, instruction: string): Promise<Page> {
    if (!page) throw new Error("Page is required");
    if (!instruction) throw new Error("Instruction is required");

    try {
      const response = await this.apiClient.post<Page>(
        "/api/v1/document-builder/enhance-selected-page",
        { page, instruction },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail ||
          error.response?.data ||
          "Failed to enhance page",
      );
    }
  }
  async downloadDocument(s3Url: string): Promise<{ download_url: string }> {
    try {
      const response = await this.apiClient.get(
        `api/v1/documents/download?url=${encodeURIComponent(s3Url)}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async deleteDocument(documentId: string) {
    try {
      // Use query parameter to handle S3 key paths with slashes
      const response = await this.apiClient.delete("api/v1/admin/documents", {
        params: { document_id: documentId },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to delete document",
      );
    }
  }
  async listDocuments(folderPath: string = "/"): Promise<Document[]> {
    try {
      const response = await this.apiClient.get("api/v1/admin/documents", {
        params: { folder_path: folderPath },
      });

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to list documents",
      );
    }
  }
  async uploadDocuments(files: File[], folderPath: string = "/"): Promise<any> {
    try {
      const formData = new FormData();

      // Add all files (include filename so FastAPI recognizes UploadFile entries)
      files.forEach((file) => {
        formData.append("files", file, file.name);
      });

      // Add folder path and organization ID
      formData.append("folder_path", folderPath);

      // Do NOT set Content-Type manually; let the browser set multipart boundary
      // Ensure binary data is preserved
      const response = await this.apiClient.post(
        "/api/v1/admin/documents/upload",
        formData,
        {
          headers: {
            Accept: "application/json",
          },
          transformRequest: [(data) => data], // Don't transform the FormData
        },
      );

      return response.data;
    } catch (error: any) {
      const detail = error?.response?.data?.detail ?? error?.response?.data;
      throw new Error(
        detail
          ? typeof detail === "string"
            ? detail
            : JSON.stringify(detail)
          : "Failed to upload documents",
      );
    }
  }
}

/* =======================
   Export Singleton
======================= */

export const documentBuilderService = new DocumentBuilderService();
